<template>
  <div class="barchart">
    <h2 v-text="snippets.BarChart2.title"></h2>
    <div class="bar-chart-filters">
      <b-field>
        <p class="control">
          <b-select
            aria-role="list"
            v-model="filterContest"
            :placeholder="snippets.BarChart2.placeholderFilterContest"
          >
            <option v-for="item in filterList" :value="item" :key="item.id">
              {{ item.label }}
            </option>
          </b-select>
        </p>
        <p class="control">
          <b-button
            label="X"
            :disabled="!filterContest"
            :title="snippets.clearButtonTitle"
            @click="filterContest = null"
          />
        </p>
      </b-field>
    </div>
    <Bar
      :chart-options="chartOptions"
      :chart-data="chartData"
      :chart-id="chartId"
      :dataset-id-key="datasetIdKey"
      :plugins="plugins"
      :css-classes="cssClasses"
      :styles="styles"
      :width="width"
      :height="height"
    />
  </div>
</template>

<script>
import { mapState } from "vuex";
import { Bar } from "vue-chartjs/legacy";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
} from "chart.js";

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale
);

export default {
  name: "BarChart2",
  components: {
    Bar,
  },
  props: {
    chartId: {
      type: String,
      default: "bar-chart2",
    },
    datasetIdKey: {
      type: String,
      default: "label",
    },
    width: {
      type: Number,
      default: 400,
    },
    cssClasses: {
      default: "",
      type: String,
    },
    styles: {
      type: Object,
      default: () => {},
    },
    plugins: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      contests: [],
      teams: [],
      filterContest: null,
      bgColorForChart: [
        "",
        "#e2231a",
        "#008542",
        "#2e2a25",
        "#85407a",
        "#23c471",
        "#b5b5b5",
        "#5fa6ad",
        "#14747a",
        "#616161",
        "#bf60b9",
        "#e69ede",
        "#92d1b1",
      ],
      chartData: {
        datasets: [],
      },
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: "y",
        scales: {
          y: {
            stacked: true,
            suggestedMin: 20,
            suggestedMax: 50,
          },
          x: {
            stacked: true,
          },
        },
        plugins: {
          legend: {
            display: true,
            position: "bottom",
            labels: {
              color: "rgb(0, 0, 0)",
            },
          },
        },
      },
    };
  },
  computed: {
    ...mapState(["snippets"]),

    height() {
      return 70 + this.teams.length * 40;
    },

    filterList: function () {
      let contests = this.contests.filter(
        (contest) => contest.cont_subcont !== "11_0"
      );
      let filterList = contests.map((contest) => {
        return { id: contest.cont_subcont, label: contest.contest_name };
      });
      filterList.push({
        id: "bonus",
        label: this.snippets.BarChart2.filterList.bonus,
      });
      filterList.push({
        id: "penalty",
        label: this.snippets.BarChart2.filterList.penalty,
      });
      return filterList;
    },
  },
  created() {
    this.contests = this.$store.getters.contests;
    this.teams = this.$store.getters.teams;
    this.updateChart(null);
  },
  watch: {
    filterContest(contest) {
      this.updateChart(contest?.id || null);
    },
  },
  methods: {
    getTeamScore(contestID, team, scoreKey = "final_points") {
      if (team.scores) {
        const score = team.scores.find(
          (s) => s.marker === team.team + "_" + contestID
        );
        if (score) {
          return 0.0 + (score[scoreKey] + 0.0);
        }
      }
      return 0.0;
    },
    getScoresForBarChart(contestID, data) {
      const newBarChartData = [];
      if (contestID) {
        const scoreProperty =
          { bonus: "bonusScore", penalty: "penaltyScore" }[contestID] ??
          "score";
        let label = "unknown";
        if (scoreProperty !== "score") {
          label = this.filterList.find((item) => item.id === contestID).label;
        }
        const splittedContestId = contestID.split("_")[0];
        const filteredData = Object.fromEntries(
          Object.entries(data).filter(
            ([key]) => key.split("_")[0] === splittedContestId
          )
        );
        for (const currentData in filteredData) {
          let bgColorIndex = currentData.split("_");

          const currentContestIdContestObject = (
            this.contests.find(
              (contest) => +contest.contest === +splittedContestId
            )?.sub_contests || []
          ).find((elem) => elem.cont_subcont === currentData);

          newBarChartData.push({
            label: currentContestIdContestObject?.sub_contest_name || label,
            data: filteredData[currentData]
              .filter((item) => {
                // eslint-disable-next-line no-prototype-builtins
                return item.hasOwnProperty(scoreProperty);
              })
              .map((item) => Math.floor(item[scoreProperty])),
            backgroundColor:
              this.bgColorForChart[
                bgColorIndex[1] ?? { bonus: 11, penalty: 12 }[currentData]
              ],
          });
        }
      } else {
        const tmp = {};
        Object.entries(data).map((subData) => {
          const contestNr = subData[0].split("_")[0];
          // eslint-disable-next-line no-prototype-builtins
          if (!tmp.hasOwnProperty(contestNr)) {
            tmp[contestNr] = {};
          }
          const scoreProperty =
            { bonus: "bonusScore", penalty: "penaltyScore" }[contestNr] ??
            "score";
          subData[1].map((team) => {
            // eslint-disable-next-line no-prototype-builtins
            if (!tmp[contestNr].hasOwnProperty(team.teamId)) {
              tmp[contestNr][team.teamId] = 0.0;
            }
            tmp[contestNr][team.teamId] += 0.0 + (team[scoreProperty] ?? 0.0);
          });
        });
        Object.entries(tmp).map((entry) => {
          let contestNr = entry[0];
          let points = entry[1];
          let label = null;
          let colorIndex = contestNr;
          if (contestNr === "bonus" || contestNr === "penalty") {
            label = this.filterList.find((item) => item.id === contestNr).label;
            colorIndex = { bonus: 11, penalty: 12 }[contestNr];
            contestNr = 11;
          }
          const contest = this.contests.find(
            (contest) => +contest.contest === +contestNr
          );
          newBarChartData.push({
            label: label ?? (contest?.contest_name || "unknown"),
            data: Object.entries(points).map((entry) => Math.floor(entry[1])),
            backgroundColor: this.bgColorForChart[colorIndex],
          });
        });
      }
      return newBarChartData;
    },
    updateChart(contestId) {
      // example: contestId = 1_0
      let subContestScores = {};
      for (const contestItem of this.contests) {
        if (contestItem.sub_contests) {
          for (const subContest of contestItem.sub_contests) {
            if (subContest.cont_subcont) {
              if (
                Object.prototype.hasOwnProperty.call(
                  subContestScores,
                  subContest.cont_subcont
                )
                // eslint-disable-next-line no-empty
              ) {
              } else {
                subContestScores[subContest.cont_subcont] = [];
              }
            }
          }
        }
      }
      const bonusPenaltyContestId = "11_0";

      for (const team of this.teams) {
        for (const subContest in subContestScores) {
          const score = this.getTeamScore(subContest, team);
          subContestScores[subContest].push({ teamId: team.team, score });
        }
        const bonusScore = this.getTeamScore(
          bonusPenaltyContestId,
          team,
          "bonus_points"
        );
        if (!subContestScores["bonus"]) {
          subContestScores["bonus"] = [];
        }
        subContestScores["bonus"].push({ teamId: team.team, bonusScore });
        if (!subContestScores["penalty"]) {
          subContestScores["penalty"] = [];
        }
        const penaltyScore = this.getTeamScore(
          bonusPenaltyContestId,
          team,
          "penalty_points"
        );
        subContestScores["penalty"].push({ teamId: team.team, penaltyScore });
      }
      //contest == 1_0 | null
      const subcontestsData = this.getScoresForBarChart(
        contestId,
        subContestScores
      );
      this.chartData.labels = this.teams.map((team) => team.team_name);
      this.chartData.datasets = subcontestsData;
    },
  },
};
</script>

<style>
.barchart {
  margin: 3rem auto auto;
}
.barchart .bar-chart-filters {
  margin: 0.5rem 0;
  display: flex;
  justify-content: center;
}
</style>
